import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Avatar,
  Button,
  Tabs,
  Tag,
  Divider,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Timeline,
  Typography,
  Switch,
  Result,
  Alert
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  CameraOutlined,
  HeartOutlined,
  StarOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  MailOutlined,
  SmileOutlined,
  CloudOutlined,
  BulbOutlined,
  TrophyOutlined,
  LoginOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom'; // 添加导航
import './index.css';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const About = () => {
  const navigate = useNavigate(); // 添加导航
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [userData, setUserData] = useState(null); // 用户数据状态
  
  // 检查登录状态
  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        setUserData(JSON.parse(savedUser));
      } catch (error) {
        console.error('解析用户数据失败:', error);
      }
    }
  }, []);
  
  // 从localStorage加载个性化数据的函数
  const loadFromStorage = (key, defaultValue) => {
    if (!userData) return defaultValue;
    const saved = localStorage.getItem(`about_${userData.userId}_${key}`);
    return saved ? JSON.parse(saved) : defaultValue;
  };

  const saveToStorage = (key, value) => {
    if (!userData) return;
    localStorage.setItem(`about_${userData.userId}_${key}`, JSON.stringify(value));
  };

  // 可爱的日记风格数据 - 使用用户信息初始化
  const [aboutData, setAboutData] = useState(() => {
    // 默认数据
    const defaultData = {
      basicInfo: {
        nickname: '小可爱日记本',
        avatar: 'https://i.pravatar.cc/300',
        tagline: '记录每一天的小确幸 ✨',
        motto: '今天也要加油鸭！',
        startDate: '2023年3月20日',
        location: '温馨小窝',
        email: 'diary@cute.com',
        signature: 'ฅ^•ﻌ•^ฅ'
      },
      features: [
        { icon: '☀️', title: '晴天模式', description: '阳光明媚的记录时光', active: true },
        { icon: '🌙', title: '夜间模式', description: '安静的思考时刻', active: false },
        { icon: '🌸', title: '春季主题', description: '粉嫩可爱的界面', active: false },
        { icon: '🎵', title: '背景音乐', description: '轻柔的钢琴曲', active: false }
      ],
      hobbies: [
        { name: '写日记', level: 5, emoji: '📝' },
        { name: '喝奶茶', level: 4, emoji: '🧋' },
        { name: '撸猫猫', level: 5, emoji: '🐱' },
        { name: '追剧', level: 3, emoji: '📺' },
        { name: '烘焙', level: 2, emoji: '🍰' }
      ],
      achievements: [
        { icon: '📖', title: '连续记录30天', date: '2023.04.20', unlocked: true },
        { icon: '💖', title: '写下第100篇日记', date: '2023.06.15', unlocked: true },
        { icon: '🌈', title: '收集7种心情标签', date: '2023.08.01', unlocked: true },
        { icon: '🏆', title: '年度日记达人', date: '2023.12.31', unlocked: false }
      ],
      moodTimeline: [
        { date: '今天', mood: '😊', event: '发现了超好吃的蛋糕店' },
        { date: '昨天', mood: '🎉', event: '和朋友庆祝生日派对' },
        { date: '前天', mood: '📚', event: '读完了一本有趣的书' },
        { date: '上周', mood: '🌸', event: '公园的樱花都开了' }
      ],
      stickers: [
        { emoji: '🐶', desc: '汪汪队立大功' },
        { emoji: '🐱', desc: '喵星人观察日记' },
        { emoji: '🐰', desc: '胡萝卜爱好者' },
        { emoji: '🦊', desc: '聪明的小狐狸' },
        { emoji: '🐻', desc: '温暖的熊抱' },
        { emoji: '🐼', desc: '国宝级可爱' }
      ],
      themeColors: {
        primary: '#ff85c0',    // 粉色
        secondary: '#ffd666',  // 黄色
        accent: '#5cdbd3',     // 青色
        background: '#fff0f6'  // 浅粉色背景
      }
    };

    // 如果用户已登录，加载个性化数据
    if (userData) {
      const isGuest = userData.isGuest;
      
      return {
        basicInfo: loadFromStorage('basicInfo', {
          nickname: userData.nickname || defaultData.basicInfo.nickname,
          avatar: userData.avatar || defaultData.basicInfo.avatar,
          tagline: userData.tagline || defaultData.basicInfo.tagline,
          motto: userData.motto || defaultData.basicInfo.motto,
          startDate: userData.startDate || defaultData.basicInfo.startDate,
          location: userData.location || defaultData.basicInfo.location,
          email: userData.email || defaultData.basicInfo.email,
          signature: userData.signature || defaultData.basicInfo.signature
        }),
        features: loadFromStorage('features', isGuest ? [
          { icon: '☀️', title: '晴天模式', description: '阳光明媚的记录时光', active: true },
          ...defaultData.features.slice(1).map(f => ({ ...f, active: false }))
        ] : defaultData.features),
        hobbies: loadFromStorage('hobbies', defaultData.hobbies),
        achievements: loadFromStorage('achievements', isGuest ? [
          { icon: '👀', title: '游客参观', date: '今天', unlocked: true },
          { icon: '🌟', title: '体验日记本', date: '今天', unlocked: true }
        ] : defaultData.achievements),
        moodTimeline: loadFromStorage('moodTimeline', defaultData.moodTimeline),
        stickers: loadFromStorage('stickers', isGuest ? [
          { emoji: '🐶', desc: '汪汪队立大功' },
          { emoji: '🐱', desc: '喵星人观察日记' }
        ] : defaultData.stickers),
        themeColors: loadFromStorage('themeColors', isGuest ? {
          primary: '#888888',    // 灰色
          secondary: '#cccccc',  // 浅灰色
          accent: '#999999',     // 中灰色
          background: '#f5f5f5'  // 浅灰色背景
        } : defaultData.themeColors)
      };
    }
    
    return defaultData;
  });

  // 保存所有数据到localStorage
  const saveAllData = (newData) => {
    if (!userData || userData.isGuest) {
      message.warning('游客模式功能受限，请登录后体验完整功能');
      return;
    }
    setAboutData(newData);
    Object.keys(newData).forEach(key => {
      saveToStorage(key, newData[key]);
    });
  };

  // 如果没有登录，显示提示
  if (!userData || !userData.loggedIn) {
    return (
      <div className="not-logged-in-container">
        <Card className="login-prompt-card">
          <Result
            icon={<LockOutlined style={{ fontSize: 64, color: '#ff85c0' }} />}
            title="日记本被锁住啦 🔒"
            subTitle="需要登录才能查看和编辑你的专属日记本"
            extra={[
              <Button
                type="primary"
                key="login"
                onClick={() => navigate('/')}
                icon={<LoginOutlined />}
                size="large"
              >
                去登录
              </Button>,
              <Button
                key="guest"
                onClick={() => {
                  // 快速游客登录
                  const userId = `guest_${Date.now()}`;
                  const guestData = {
                    userId,
                    username: '小游客',
                    loggedIn: true,
                    isGuest: true,
                    loginTime: new Date().toISOString(),
                    avatar: 'https://i.pravatar.cc/150?img=32',
                    nickname: '好奇的游客',
                    tagline: '我只是来参观一下的~',
                    motto: '悄悄来，悄悄走',
                    startDate: new Date().toLocaleDateString('zh-CN'),
                    location: '游客中心',
                    email: 'visitor@diary.com',
                    signature: '👀'
                  };
                  localStorage.setItem('currentUser', JSON.stringify(guestData));
                  setUserData(guestData);
                  message.info('欢迎游客！可以体验基础功能哦~');
                }}
                size="large"
              >
                游客体验
              </Button>
            ]}
          />
          
          <Alert
            message="登录后你可以："
            description={
              <ul style={{ margin: '12px 0 0 20px' }}>
                <li>✨ 个性化你的日记本封面</li>
                <li>🌈 自定义颜色主题和贴纸</li>
                <li>📝 保存你的爱好和成就</li>
                <li>💾 永久保存你的日记数据</li>
                <li>🔒 保护你的隐私和回忆</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginTop: 32 }}
          />
        </Card>
      </div>
    );
  }
  
  // 游客提示
  const isGuest = userData.isGuest;
  
  // 上传头像
  const handleAvatarUpload = (info) => {
    if (isGuest) {
      message.warning('游客不能修改头像，请登录后使用');
      return;
    }
    
    if (info.file.status === 'uploading') return;
    
    if (info.file.status === 'done') {
      const file = info.file.originFileObj;
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const base64Image = e.target.result;
        const newData = {
          ...aboutData,
          basicInfo: {
            ...aboutData.basicInfo,
            avatar: base64Image
          }
        };
        saveAllData(newData);
        message.success('头像更新成功！');
      };
      
      reader.readAsDataURL(file);
    }
  };

  const uploadProps = {
    name: 'avatar',
    accept: 'image/*',
    showUploadList: false,
    beforeUpload: (file) => {
      if (isGuest) {
        message.warning('游客不能上传头像，请登录后使用');
        return false;
      }
      
      const isImage = file.type.startsWith('image/');
      const isLt2M = file.size / 1024 / 1024 < 2;
      
      if (!isImage) {
        message.error('只能上传图片哦！');
        return false;
      }
      if (!isLt2M) {
        message.error('图片太大啦，请选择小于2MB的图片');
        return false;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const newData = {
          ...aboutData,
          basicInfo: {
            ...aboutData.basicInfo,
            avatar: e.target.result
          }
        };
        saveAllData(newData);
        message.success('头像已换上新的啦！');
      };
      reader.readAsDataURL(file);
      
      return false;
    }
  };

  // 编辑整个About页面
  const handleEditAbout = (values) => {
    if (isGuest) {
      message.warning('游客不能修改设置，请登录后使用');
      setEditModalVisible(false);
      return;
    }
    
    setLoading(true);
    
    // 处理表单数据
    const newBasicInfo = {
      ...aboutData.basicInfo,
      nickname: values.nickname || aboutData.basicInfo.nickname,
      tagline: values.tagline || aboutData.basicInfo.tagline,
      motto: values.motto || aboutData.basicInfo.motto,
      startDate: values.startDate || aboutData.basicInfo.startDate,
      location: values.location || aboutData.basicInfo.location,
      email: values.email || aboutData.basicInfo.email,
      signature: values.signature || aboutData.basicInfo.signature
    };

    const newData = {
      ...aboutData,
      basicInfo: newBasicInfo,
      themeColors: values.themeColors || aboutData.themeColors
    };

    setTimeout(() => {
      saveAllData(newData);
      setLoading(false);
      setEditModalVisible(false);
      message.success('日记本设置更新成功！');
    }, 800);
  };

  // 添加新的爱好
  const handleAddHobby = () => {
    if (isGuest) {
      message.warning('游客不能添加爱好，请登录后使用');
      return;
    }
    
    const newHobby = { name: '新爱好', level: 3, emoji: '⭐' };
    const newHobbies = [...aboutData.hobbies, newHobby];
    const newData = { ...aboutData, hobbies: newHobbies };
    saveAllData(newData);
    message.success('添加了新爱好！记得去编辑哦~');
  };

  // 更新爱好等级
  const handleUpdateHobbyLevel = (index, newLevel) => {
    if (isGuest) {
      message.warning('游客不能修改爱好，请登录后使用');
      return;
    }
    
    const newHobbies = [...aboutData.hobbies];
    newHobbies[index].level = newLevel;
    const newData = { ...aboutData, hobbies: newHobbies };
    saveAllData(newData);
  };

  // 切换特性开关
  const toggleFeature = (index) => {
    if (isGuest && index > 0) {
      message.warning('游客只能开启晴天模式，其他功能请登录后使用');
      return;
    }
    
    const newFeatures = [...aboutData.features];
    newFeatures[index].active = !newFeatures[index].active;
    const newData = { ...aboutData, features: newFeatures };
    saveAllData(newData);
  };

  // 更新心情事件
  const handleUpdateMood = (index, newEvent) => {
    if (isGuest) {
      message.warning('游客不能编辑心情，请登录后使用');
      return;
    }
    
    const newTimeline = [...aboutData.moodTimeline];
    newTimeline[index].event = newEvent;
    const newData = { ...aboutData, moodTimeline: newTimeline };
    saveAllData(newData);
  };

  // 登出功能
  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    message.success('已退出登录');
    navigate('/');
  };

  return (
    <div className="cute-about-container" style={{
      '--primary-color': aboutData.themeColors.primary,
      '--secondary-color': aboutData.themeColors.secondary,
      '--accent-color': aboutData.themeColors.accent,
      '--background-color': aboutData.themeColors.background
    }}>
      {/* 顶部用户信息栏 */}
      <div className="user-info-bar">
        <div className="user-info">
          <Avatar 
            size="small" 
            src={aboutData.basicInfo.avatar}
            icon={<UserOutlined />}
          />
          <Text className="username">
            {userData.username}
            {isGuest && <Tag color="default" size="small" style={{ marginLeft: 8 }}>游客</Tag>}
          </Text>
        </div>
        <Button 
          type="text" 
          size="small"
          onClick={handleLogout}
        >
          退出登录
        </Button>
      </div>

      {/* 顶部可爱卡片 */}
      <Card className="cute-header-card">
        <div className="header-background">
          <div className="floating-emoji">🌸</div>
          <div className="floating-emoji" style={{ top: '20%', right: '15%' }}>✨</div>
          <div className="floating-emoji" style={{ bottom: '30%', left: '10%' }}>💫</div>
          
          <Row gutter={[32, 32]} align="middle">
            <Col xs={24} md={8} className="avatar-column">
              <div className="cute-avatar-section">
                <Upload {...uploadProps}>
                  <div className="avatar-wrapper">
                    <Avatar
                      size={150}
                      src={aboutData.basicInfo.avatar}
                      icon={<UserOutlined />}
                      className="cute-avatar"
                    />
                    {!isGuest && (
                      <div className="camera-sticker">
                        <CameraOutlined />
                      </div>
                    )}
                  </div>
                </Upload>
                <div className="nickname-badge">
                  <SmileOutlined />
                  <span>{aboutData.basicInfo.nickname}</span>
                </div>
                <div className="signature">
                  {aboutData.basicInfo.signature}
                </div>
              </div>
            </Col>

            <Col xs={24} md={16}>
              <div className="intro-card">
                <div className="intro-header">
                  <h1 className="cute-title">{aboutData.basicInfo.tagline}</h1>
                  {!isGuest && (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      className="edit-about-btn"
                      onClick={() => setEditModalVisible(true)}
                      shape="round"
                    >
                      个性化设置
                    </Button>
                  )}
                </div>
                
                <div className="motto-box">
                  <BulbOutlined className="motto-icon" />
                  <Paragraph className="cute-motto">
                    {aboutData.basicInfo.motto}
                  </Paragraph>
                </div>
                
                <Divider className="cute-divider" />
                
                <div className="info-stickers">
                  <div className="info-sticker">
                    <CalendarOutlined />
                    <Text>始于 {aboutData.basicInfo.startDate}</Text>
                  </div>
                  <div className="info-sticker">
                    <EnvironmentOutlined />
                    <Text>{aboutData.basicInfo.location}</Text>
                  </div>
                  <div className="info-sticker">
                    <MailOutlined />
                    <Text>{aboutData.basicInfo.email}</Text>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Card>

      {/* 游客提示 */}
      {isGuest && (
        <Alert
          message="游客模式提示"
          description="您正在使用游客体验模式，部分功能受限。请登录后享受完整的个性化功能和数据永久保存。"
          type="warning"
          showIcon
          action={
            <Button 
              size="small" 
              type="primary"
              onClick={() => navigate('/')}
            >
              立即登录
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 内容标签页 */}
      <Tabs defaultActiveKey="features" className="cute-tabs">
        <TabPane tab={<span><StarOutlined /> 日记本特性</span>} key="features">
          <Card className="cute-content-card">
            <div className="features-grid">
              {aboutData.features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">
                    <span style={{ fontSize: 32 }}>{feature.icon}</span>
                  </div>
                  <div className="feature-content">
                    <h4>{feature.title}</h4>
                    <Text type="secondary">{feature.description}</Text>
                    <div className="feature-switch">
                      <Switch
                        checked={feature.active}
                        onChange={() => toggleFeature(index)}
                        size="small"
                        className="cute-switch"
                        disabled={isGuest && index > 0}
                      />
                      <span className="switch-label">
                        {feature.active ? '已开启' : '已关闭'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabPane>

        <TabPane tab={<span><HeartOutlined /> 我的爱好</span>} key="hobbies">
          <Card className="cute-content-card">
            <div className="hobbies-section">
              <Title level={4} className="section-title">
                兴趣爱好收集册
                {!isGuest && (
                  <Button 
                    type="dashed" 
                    size="small" 
                    onClick={handleAddHobby}
                    className="add-button"
                  >
                    + 添加爱好
                  </Button>
                )}
              </Title>
              
              <div className="hobbies-grid">
                {aboutData.hobbies.map((hobby, index) => (
                  <div key={index} className="hobby-card">
                    <div className="hobby-emoji">
                      <span style={{ fontSize: 40 }}>{hobby.emoji}</span>
                    </div>
                    <div className="hobby-info">
                      <Text strong style={{ fontSize: 16 }}>{hobby.name}</Text>
                      <div className="level-control">
                        <Text type="secondary">喜爱程度：</Text>
                        <div className="stars">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span
                              key={star}
                              className={`star ${star <= hobby.level ? 'filled' : ''}`}
                              onClick={() => !isGuest && handleUpdateHobbyLevel(index, star)}
                              style={{ cursor: isGuest ? 'not-allowed' : 'pointer' }}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        <Text className="level-text">{hobby.level}颗星</Text>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabPane>

        <TabPane tab={<span><TrophyOutlined /> 成就墙</span>} key="achievements">
          <Card className="cute-content-card">
            <div className="achievements-wall">
              {aboutData.achievements.map((achievement, index) => (
                <div 
                  key={index} 
                  className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}
                >
                  <div className="achievement-icon">
                    <span style={{ fontSize: 36 }}>{achievement.icon}</span>
                  </div>
                  <div className="achievement-info">
                    <Text strong className="achievement-title">
                      {achievement.title}
                    </Text>
                    <Text type="secondary" className="achievement-date">
                      {achievement.date}
                    </Text>
                    <div className="achievement-status">
                      {achievement.unlocked ? (
                        <Tag color="green" icon={<StarOutlined />}>
                          已解锁
                        </Tag>
                      ) : (
                        <Tag color="default" icon={<StarOutlined />}>
                          待解锁
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabPane>

        <TabPane tab={<span><CloudOutlined /> 心情轨迹</span>} key="mood">
          <Card className="cute-content-card">
            <Timeline className="cute-timeline">
              {aboutData.moodTimeline.map((mood, index) => (
                <Timeline.Item 
                  key={index}
                  dot={
                    <div className="mood-dot">
                      <span style={{ fontSize: 24 }}>{mood.mood}</span>
                    </div>
                  }
                  color="pink"
                >
                  <div className="mood-item">
                    <div className="mood-header">
                      <Text strong>{mood.date}</Text>
                      {!isGuest && (
                        <Button 
                          type="text" 
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => {
                            const newEvent = prompt('编辑事件：', mood.event);
                            if (newEvent) handleUpdateMood(index, newEvent);
                          }}
                        >
                          编辑
                        </Button>
                      )}
                    </div>
                    <div className="mood-event">
                      {mood.event}
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </TabPane>

        <TabPane tab={<span>🌈 小贴纸</span>} key="stickers">
          <Card className="cute-content-card">
            <div className="stickers-collection">
              {aboutData.stickers.map((sticker, index) => (
                <div key={index} className="sticker-item">
                  <div className="sticker-emoji">
                    <span style={{ fontSize: 48 }}>{sticker.emoji}</span>
                  </div>
                  <div className="sticker-desc">
                    <Text type="secondary">{sticker.desc}</Text>
                  </div>
                </div>
              ))}
            </div>
            
            {!isGuest && (
              <>
                <Divider className="cute-divider" />
                
                <div className="theme-customizer">
                  <Title level={5} style={{ marginBottom: 16 }}>
                    颜色主题定制
                  </Title>
                  <div className="color-palette">
                    <div className="color-item">
                      <div 
                        className="color-preview" 
                        style={{ backgroundColor: aboutData.themeColors.primary }}
                      />
                      <Text>主色调</Text>
                    </div>
                    <div className="color-item">
                      <div 
                        className="color-preview" 
                        style={{ backgroundColor: aboutData.themeColors.secondary }}
                      />
                      <Text>辅助色</Text>
                    </div>
                    <div className="color-item">
                      <div 
                        className="color-preview" 
                        style={{ backgroundColor: aboutData.themeColors.accent }}
                      />
                      <Text>点缀色</Text>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {/* 编辑模态框 */}
      {!isGuest && (
        <Modal
          title="🎀 个性化你的日记本"
          open={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          footer={null}
          width={700}
          className="cute-edit-modal"
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={aboutData.basicInfo}
            onFinish={handleEditAbout}
          >
            <div className="edit-sections">
              <div className="edit-section">
                <Title level={5} className="section-label">基本资料</Title>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="昵称"
                      name="nickname"
                    >
                      <Input placeholder="你的可爱昵称" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="个性签名"
                      name="tagline"
                    >
                      <Input placeholder="一句话介绍自己" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label="今日格言"
                      name="motto"
                    >
                      <TextArea rows={2} placeholder="今天想对自己说的话" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="开始日期"
                      name="startDate"
                    >
                      <Input placeholder="什么时候开始写日记的？" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="所在地"
                      name="location"
                    >
                      <Input placeholder="现在在哪里呢？" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="邮箱"
                      name="email"
                      rules={[{ type: 'email', message: '请输入有效的邮箱' }]}
                    >
                      <Input placeholder="联系邮箱" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="个性尾巴"
                      name="signature"
                    >
                      <Input placeholder="比如：ฅ^•ﻌ•^ฅ" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Divider className="cute-divider" />

              <div className="edit-section">
                <Title level={5} className="section-label">颜色主题</Title>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label="主色调"
                      name={['themeColors', 'primary']}
                    >
                      <Input type="color" defaultValue={aboutData.themeColors.primary} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="辅助色"
                      name={['themeColors', 'secondary']}
                    >
                      <Input type="color" defaultValue={aboutData.themeColors.secondary} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="点缀色"
                      name={['themeColors', 'accent']}
                    >
                      <Input type="color" defaultValue={aboutData.themeColors.accent} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
            
            <div className="modal-footer">
              <Button onClick={() => setEditModalVisible(false)} style={{ marginRight: 8 }}>
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                className="save-button"
              >
                保存设置
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default About;